import { ROUTES } from "app-router/routes";

export const ADMIN_ROLES = {
  ADMIN: "ADMIN",
  EDITOR: "EDITOR",
  READ_ONLY: "READ_ONLY",
} as const;

export type AdminRole = (typeof ADMIN_ROLES)[keyof typeof ADMIN_ROLES];

export const ADMIN_PERMISSIONS = {
  ANALYTICS_VIEW: "analytics:view",
  ADMINS_READ: "admins:read",
  ADMINS_WRITE: "admins:write",
  USERS_READ: "users:read",
  USERS_WRITE: "users:write",
  TEST_REQUESTS_READ: "test_requests:read",
  TEST_REQUESTS_WRITE: "test_requests:write",
  SUPPLEMENTS_READ: "supplements:read",
  SUPPLEMENTS_CREATE: "supplements:create",
  SUPPLEMENTS_UPDATE: "supplements:update",
  SUPPLEMENTS_DELETE: "supplements:delete",
  SUPPLEMENTS_FEATURE: "supplements:feature",
  CATALOG_READ: "catalog:read",
  CATALOG_WRITE: "catalog:write",
  SCANS_READ: "scans:read",
  LABS_READ: "labs:read",
  LABS_WRITE: "labs:write",
  NOTIFICATIONS_READ: "notifications:read",
  NOTIFICATIONS_WRITE: "notifications:write",
  CONTENT_READ: "content:read",
  CONTENT_WRITE: "content:write",
  EXTERNAL_SUPPLEMENTS_READ: "external_supplements:read",
  EXTERNAL_SUPPLEMENTS_WRITE: "external_supplements:write",
  PROMO_CODES_READ: "promo_codes:read",
  PROMO_CODES_WRITE: "promo_codes:write",
  SETTINGS_WRITE: "settings:write",
} as const;

export type AdminPermission =
  (typeof ADMIN_PERMISSIONS)[keyof typeof ADMIN_PERMISSIONS];

export type PermissionSubject = {
  permissions?: string[] | null;
  role?: string | null;
};

const READ_ONLY_PERMISSIONS: AdminPermission[] = [
  ADMIN_PERMISSIONS.ADMINS_READ,
  ADMIN_PERMISSIONS.USERS_READ,
  ADMIN_PERMISSIONS.TEST_REQUESTS_READ,
  ADMIN_PERMISSIONS.SUPPLEMENTS_READ,
  ADMIN_PERMISSIONS.CATALOG_READ,
  ADMIN_PERMISSIONS.SCANS_READ,
  ADMIN_PERMISSIONS.LABS_READ,
  ADMIN_PERMISSIONS.NOTIFICATIONS_READ,
  ADMIN_PERMISSIONS.CONTENT_READ,
  ADMIN_PERMISSIONS.EXTERNAL_SUPPLEMENTS_READ,
  ADMIN_PERMISSIONS.PROMO_CODES_READ,
];

const ROLE_PERMISSIONS: Record<AdminRole, string[]> = {
  [ADMIN_ROLES.ADMIN]: ["*"],
  [ADMIN_ROLES.EDITOR]: [
    ...READ_ONLY_PERMISSIONS,
    ADMIN_PERMISSIONS.TEST_REQUESTS_WRITE,
    ADMIN_PERMISSIONS.SUPPLEMENTS_CREATE,
    ADMIN_PERMISSIONS.SUPPLEMENTS_UPDATE,
  ],
  [ADMIN_ROLES.READ_ONLY]: READ_ONLY_PERMISSIONS,
};

export const ROUTE_PERMISSIONS: Record<string, AdminPermission[]> = {
  [ROUTES.ADMIN_USERS]: [ADMIN_PERMISSIONS.ADMINS_READ],
  [ROUTES.ADD_BRAND]: [ADMIN_PERMISSIONS.CATALOG_WRITE],
  [ROUTES.ADD_SUPPLEMENT]: [ADMIN_PERMISSIONS.SUPPLEMENTS_CREATE],
  [ROUTES.BANNERS]: [ADMIN_PERMISSIONS.CONTENT_READ],
  [ROUTES.BRAND_DETAILS]: [ADMIN_PERMISSIONS.CATALOG_READ],
  [ROUTES.BRANDS]: [ADMIN_PERMISSIONS.CATALOG_READ],
  [ROUTES.CATEGORIES]: [ADMIN_PERMISSIONS.CATALOG_READ],
  [ROUTES.CATEGORY_DETAILS]: [ADMIN_PERMISSIONS.CATALOG_READ],
  [ROUTES.DASHBOARD]: [ADMIN_PERMISSIONS.ANALYTICS_VIEW],
  [ROUTES.MANAGE_NOTIFICATIONS]: [ADMIN_PERMISSIONS.NOTIFICATIONS_WRITE],
  [ROUTES.MANAGE_POTENCY]: [ADMIN_PERMISSIONS.CATALOG_READ],
  [ROUTES.PAID_TEST_REQUESTS]: [ADMIN_PERMISSIONS.TEST_REQUESTS_READ],
  [ROUTES.PRODUCT_TYPES]: [ADMIN_PERMISSIONS.CATALOG_READ],
  [ROUTES.PROMO_BANNERS]: [ADMIN_PERMISSIONS.CONTENT_READ],
  [ROUTES.PROMO_CODES]: [ADMIN_PERMISSIONS.PROMO_CODES_READ],
  [ROUTES.SCANS]: [ADMIN_PERMISSIONS.SCANS_READ],
  [ROUTES.SUPPLEMENT_DETAILS]: [ADMIN_PERMISSIONS.SUPPLEMENTS_READ],
  [ROUTES.SUPPLEMENTS]: [ADMIN_PERMISSIONS.SUPPLEMENTS_READ],
  [ROUTES.TEST_REQUESTS]: [ADMIN_PERMISSIONS.TEST_REQUESTS_READ],
  [ROUTES.TOP_REQUESTED_SUPPLEMENTS]: [ADMIN_PERMISSIONS.TEST_REQUESTS_READ],
  [ROUTES.TRENDING_SUPPLEMENT_DETAILS]: [
    ADMIN_PERMISSIONS.EXTERNAL_SUPPLEMENTS_READ,
  ],
  [ROUTES.TRENDING_SUPPLEMENTS]: [
    ADMIN_PERMISSIONS.EXTERNAL_SUPPLEMENTS_READ,
  ],
  [ROUTES.USER_DETAILS]: [ADMIN_PERMISSIONS.USERS_READ],
  [ROUTES.USERS]: [ADMIN_PERMISSIONS.USERS_READ],
  [ROUTES.VIDEO_BANNERS]: [ADMIN_PERMISSIONS.CONTENT_READ],
};

const DEFAULT_ROUTE_PRIORITY = [
  ROUTES.DASHBOARD,
  ROUTES.USERS,
  ROUTES.TEST_REQUESTS,
  ROUTES.SUPPLEMENTS,
  ROUTES.CATEGORIES,
  ROUTES.BRANDS,
  ROUTES.SCANS,
  ROUTES.PRODUCT_TYPES,
  ROUTES.BANNERS,
  ROUTES.PROMO_CODES,
  ROUTES.ADMIN_USERS,
];

export const normalizeAdminRole = (
  role?: string | null
): AdminRole | null => {
  if (!role || typeof role !== "string") {
    return null;
  }

  const normalizedRole = role.trim().toUpperCase().replace(/[\s-]+/g, "_");
  const availableRoles = Object.values(ADMIN_ROLES);

  return availableRoles.includes(normalizedRole as AdminRole)
    ? (normalizedRole as AdminRole)
    : null;
};

export const getRolePermissions = (role?: string | null): string[] => {
  const normalizedRole = normalizeAdminRole(role);

  if (!normalizedRole) {
    return [];
  }

  return [...ROLE_PERMISSIONS[normalizedRole]];
};

export const normalizePermissions = (
  role?: string | null,
  permissions?: string[] | null
): string[] => {
  if (Array.isArray(permissions) && permissions.length > 0) {
    return Array.from(new Set(permissions.filter(Boolean)));
  }

  return getRolePermissions(role);
};

export const normalizePermissionSubject = <T extends PermissionSubject>(
  subject?: T | null
): (T & { permissions: string[]; role: AdminRole }) | null => {
  if (!subject) {
    return null;
  }

  const normalizedRole = normalizeAdminRole(subject.role);

  if (!normalizedRole) {
    return null;
  }

  return {
    ...subject,
    permissions: normalizePermissions(normalizedRole, subject.permissions),
    role: normalizedRole,
  };
};

export const hasPermission = (
  subject: PermissionSubject | null | undefined,
  permission?: string | null
): boolean => {
  if (!permission) {
    return true;
  }

  const permissions = normalizePermissions(subject?.role, subject?.permissions);

  return permissions.includes("*") || permissions.includes(permission);
};

export const hasAnyPermission = (
  subject: PermissionSubject | null | undefined,
  permissions?: string[] | null
): boolean => {
  if (!permissions?.length) {
    return true;
  }

  return permissions.some((permission) => hasPermission(subject, permission));
};

export const hasAllPermissions = (
  subject: PermissionSubject | null | undefined,
  permissions?: string[] | null
): boolean => {
  if (!permissions?.length) {
    return true;
  }

  return permissions.every((permission) => hasPermission(subject, permission));
};

export const getDefaultAuthorizedRoute = (
  subject: PermissionSubject | null | undefined
) => {
  const matchedRoute = DEFAULT_ROUTE_PRIORITY.find((route) =>
    hasAllPermissions(subject, ROUTE_PERMISSIONS[route])
  );

  return matchedRoute || ROUTES.LOGIN;
};
